import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  CustomInput,
  FormFeedback,
  FormGroup,
  FormText,
  Input,
  Label,
  ListGroup,
  Row,
} from 'reactstrap';
import Select from 'react-select';
import {
  CASE_PLAN_DOCUMENT_TYPE,
  DEPARTMENT_POST_MAJORITY_SUPPORT_SERVICES,
  DomainContext,
  EFT_DOCUMENT_TYPE,
  IPostMajorityDocumentStatus,
  IProgramType,
  IStage,
  NEEDS_ASSESSMENT_DOCUMENT_TYPE,
  POST_MAJORITY_PROGRAM_TYPE_OTHER,
  PROGRAM_AGREEMENT_DOCUMENT_TYPE,
  STAGE_INACTIVE,
  TSS_APPLICATION_CONSENT_DOCUMENT_TYPE,
} from '../../contexts/DomainContext';
import { FileContext, IPostMajoritySupportServicesFields } from './FileContext';
import DatePicker from 'react-datepicker';

import { RequiredLabel } from '../RequiredLabel';
import { UsersContext } from '../../contexts/UsersContext';
import { IUser } from '../Users/UsersTable';
import { MyUserContext } from '../../contexts/MyUserContext';
import { disabledSelectStylesWithMenu } from '../../utils/StylingUtils';
import { FileUpload, OnFileDrop } from '../../components/FileUpload/FileUpload';
import { IDocument, IDocumentToUpload } from '../../components/Files/Documents/Documents';
import { DocumentListItem } from '../../components/Files/Documents/DocumentListItem';
import { getJwtFromStorage } from '../../services/AuthenticationService';

export const PostMajoritySupportServicesFileDetails: React.FC<{
  isEditingAllowed: boolean;
  trySave: boolean;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
  postMajorityDocuments?: IDocument[];
  setPostMajorityDocuments?: React.Dispatch<React.SetStateAction<IDocument[]>>;
  postMajorityDocumentsToUpload: IDocumentToUpload[];
  setPostMajorityDocumentsToUpload: React.Dispatch<React.SetStateAction<IDocumentToUpload[]>>;
  setOpenUncheckConfidentialityModal: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenCheckConfidentialityModal: React.Dispatch<React.SetStateAction<boolean>>;
}> = (props) => {
  const domainContext = useContext(DomainContext);
  const fileContext = useContext(FileContext);
  const usersContext = useContext(UsersContext);
  const myUser = useContext(MyUserContext);

  const stages = domainContext.stages;
  const programTypes = domainContext.programTypes;
  const postMajorityDocumentStatuses = domainContext.postMajorityDocumentStatuses;

  const staff = usersContext.users;
  const [selectedStaff, setSelectedStaff] = useState<IUser[]>([]);

  useEffect(() => {
    setSelectedStaff(staff.filter((s) => fileContext.staffIds.includes(s.userId)));
  }, [staff, fileContext.staffIds]);

  const handleFileDrop =
    (documentType: string): OnFileDrop =>
    (acceptedFiles) => {
      if (acceptedFiles.length > 0 && props.setPostMajorityDocumentsToUpload) {
        props.setIsDirty(true);
        props.setPostMajorityDocumentsToUpload((docs) => [
          ...docs,
          ...acceptedFiles.map(
            (t) =>
              ({
                file: t,
                documentTypeId: documentType,
              } as IDocumentToUpload)
          ),
        ]);
      }
    };

  const removeDocumentToUpload = (document: File) => {
    if (props.setPostMajorityDocumentsToUpload) {
      props.setPostMajorityDocumentsToUpload((files) => {
        const filteredFiles = files.filter((file) => {
          if ('name' in file.file && 'name' in document) {
            return file.file.name !== document.name;
          }
          return false;
        });
        return filteredFiles;
      });
    }
  };

  const downloadDocument = (uploadId: string) => {
    const getUrl = (uId: string) => '/api/uploads/download/' + uId + '/' + getJwtFromStorage();
    const link = document.createElement('a');
    link.href = getUrl(uploadId);
    link.setAttribute('download', 'download');
    document.body.appendChild(link);
    link.click();
  };

  return (
    <>
      <Row>
        <Col sm={12} md={6}>
          <Card className={'mb-4'}>
            <CardHeader>Post Majority Support Services Details</CardHeader>
            <CardBody>
              <Row>
                <Col>
                  <FormGroup className={'w-100 mb-2 '}>
                    <Label for={'completed-date'}>Completed Date</Label>
                    <DatePicker
                      selected={fileContext.completedDate}
                      name={'application-date'}
                      onChange={(date) => {
                        fileContext.setCompletedDate(date ? date : undefined);
                        props.setIsDirty(true);
                      }}
                      peekNextMonth
                      showMonthDropdown
                      disabled={fileContext.stageId !== STAGE_INACTIVE || !props.isEditingAllowed}
                      showYearDropdown
                      isClearable={true}
                      dateFormat='MMM dd, yyyy'
                      dropdownMode='select'
                      className={
                        props.isEditingAllowed ? 'date-picker rounded mb-0' : 'date-picker-disabled rounded mb-0'
                      }
                      placeholderText={'MMM dd, yyyy'}
                    />
                  </FormGroup>
                </Col>
                <Col>
                  <FormGroup className={'w-100 mb-2'}>
                    <Label for={'initial-referral-date'}>Initial Referral Date</Label>
                    <DatePicker
                      selected={
                        fileContext.postMajoritySupportServicesFields &&
                        fileContext.postMajoritySupportServicesFields.initialReferralDate
                          ? new Date(fileContext.postMajoritySupportServicesFields.initialReferralDate)
                          : undefined
                      }
                      name={'initial-referral-date'}
                      onChange={(date) => {
                        fileContext.setPostMajoritySupportServicesFields(
                          (s) =>
                            ({
                              ...s,
                              initialReferralDate: date ? date.getTime() : null,
                            } as IPostMajoritySupportServicesFields)
                        );
                        props.setIsDirty(true);
                      }}
                      peekNextMonth
                      showMonthDropdown
                      showYearDropdown
                      isClearable={true}
                      dateFormat='MMM dd, yyyy'
                      dropdownMode='select'
                      className={
                        props.isEditingAllowed ? 'date-picker rounded mb-0' : 'date-picker-disabled rounded mb-0'
                      }
                      placeholderText={'MMM dd, yyyy'}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormGroup className={'w-100 mb-2'}>
                    <Label>Program Type</Label>
                    <Select
                      styles={disabledSelectStylesWithMenu}
                      name={'programTypeId'}
                      placeholder={<span className='placeholder'>Select...</span>}
                      options={programTypes}
                      value={
                        programTypes.find(
                          (s: IProgramType) =>
                            s.programTypeId === fileContext.postMajoritySupportServicesFields?.programTypeId
                        ) ?? undefined
                      }
                      type={'text'}
                      onChange={(value: any) => {
                        fileContext.setPostMajoritySupportServicesFields(
                          (s) =>
                            ({
                              ...s,
                              programTypeId: value?.programTypeId,
                            } as IPostMajoritySupportServicesFields)
                        );
                        if (
                          fileContext.postMajoritySupportServicesFields?.programTypeId !==
                          POST_MAJORITY_PROGRAM_TYPE_OTHER
                        ) {
                          fileContext.setPostMajoritySupportServicesFields(
                            (s) =>
                              ({
                                ...s,
                                otherProgramTypeDetails: undefined,
                              } as IPostMajoritySupportServicesFields)
                          );
                        }
                        props.setIsDirty(true);
                      }}
                      getOptionValue={(option: any) => option.programTypeId}
                      getOptionLabel={(option: any) => option.name}
                      isClearable={false}
                      //disabled for CYFN-434, will put back in after CYFN is done with their changes
                      //isDisabled={params.fileId !== undefined}
                    />
                  </FormGroup>
                </Col>
                <Col>
                  {fileContext.postMajoritySupportServicesFields?.programTypeId ===
                    POST_MAJORITY_PROGRAM_TYPE_OTHER && (
                    <FormGroup className={'w-100 mb-2 '}>
                      <Label>Program Type Details</Label>
                      <Input
                        type='text'
                        placeholder='Type here...'
                        name='other-program-type-details'
                        value={fileContext.postMajoritySupportServicesFields?.otherProgramTypeDetails}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          fileContext.setPostMajoritySupportServicesFields(
                            (s) =>
                              ({
                                ...s,
                                otherProgramTypeDetails: e.target.value ? e.target.value : undefined,
                              } as IPostMajoritySupportServicesFields)
                          );
                          props.setIsDirty(true);
                        }}
                        disabled={!props.isEditingAllowed}
                      />
                    </FormGroup>
                  )}
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormGroup className={'w-100 mb-2'}>
                    <Label>Eligibility Information</Label>
                    <Input
                      type='textarea'
                      placeholder=''
                      style={{ minHeight: '226px' }}
                      name='name'
                      value={fileContext.postMajoritySupportServicesFields?.eligibilityInformation}
                      disabled={!props.isEditingAllowed}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        fileContext.setPostMajoritySupportServicesFields(
                          (s) =>
                            ({
                              ...s,
                              eligibilityInformation: e.target.value ? e.target.value : '',
                            } as IPostMajoritySupportServicesFields)
                        );
                        props.setIsDirty(true);
                      }}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
        <Col sm={12} md={6}>
          <Card className={'mb-4'}>
            <CardHeader>File Details</CardHeader>
            <CardBody>
              {myUser.isSuperAdmin && (
                <Row className='square border-bottom mb-2'>
                  <Col>
                    <FormGroup>
                      <Label for={'isConfidential'}>Confidentiality</Label>
                      <CustomInput
                        type='checkbox'
                        id={'isConfidential'}
                        name={'isConfidential'}
                        label={'Highly Confidential File'}
                        checked={fileContext.isConfidential}
                        onChange={() => {
                          if (fileContext.isConfidential) {
                            props.setOpenUncheckConfidentialityModal(true);
                          } else {
                            props.setOpenCheckConfidentialityModal(true);
                          }
                          fileContext.setIsConfidential(!fileContext.isConfidential);
                          props.setIsDirty(true);
                        }}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              )}
              <Row>
                <Col>
                  <FormGroup className={'w-100 mb-2'}>
                    <RequiredLabel for={'application-date'} required={fileContext.createdDate === undefined}>
                      Created Date
                    </RequiredLabel>
                    <DatePicker
                      selected={fileContext.createdDate}
                      name={'application-date'}
                      onChange={(date) => {
                        fileContext.setCreatedDate(date ? date : undefined);
                        props.setIsDirty(true);
                      }}
                      peekNextMonth
                      showMonthDropdown
                      disabled={!props.isEditingAllowed}
                      showYearDropdown
                      isClearable={true}
                      dateFormat='MMM dd, yyyy'
                      dropdownMode='select'
                      className={
                        props.isEditingAllowed ? 'date-picker rounded mb-0' : 'date-picker-disabled rounded mb-0'
                      }
                      placeholderText={'MMM dd, yyyy'}
                    />
                    {props.trySave && fileContext.createdDate === undefined && (
                      <span style={{ fontSize: 13, color: '#B22222' }}>This field is mandatory</span>
                    )}
                  </FormGroup>
                </Col>
                <Col>
                  <FormGroup className={'w-100 mb-2 '}>
                    <RequiredLabel for={'stageId'} required={!fileContext.stageId}>
                      Stage
                    </RequiredLabel>
                    <Select
                      styles={disabledSelectStylesWithMenu}
                      name={'stageId'}
                      placeholder={<span className='placeholder'>Select...</span>}
                      options={stages.filter(
                        (s) => s.fileTypes && s.fileTypes.includes(fileContext.fileTypeId ? fileContext.fileTypeId : '')
                      )}
                      value={stages.find((s: IStage) => s.stageId === fileContext.stageId)}
                      onChange={(value: any) => {
                        fileContext.setStageId(value?.stageId || '');
                        props.setIsDirty(true);
                      }}
                      getOptionValue={(option: IStage) => option.stageId}
                      getOptionLabel={(option: IStage) => option.name}
                      isDisabled={!props.isEditingAllowed}
                    />
                    <FormFeedback>This field is mandatory</FormFeedback>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormGroup className={'w-100 mb-2'}>
                    <Label for={'staffIds'}>Staff</Label>
                    <Select
                      styles={disabledSelectStylesWithMenu}
                      name={'staffIds'}
                      placeholder={<span className='placeholder'>Select...</span>}
                      options={staff.filter(
                        (u) =>
                          (u.active || fileContext.file.staffIds?.some((e) => e === u.userId)) &&
                          u.departmentAccessIds.includes(DEPARTMENT_POST_MAJORITY_SUPPORT_SERVICES)
                      )}
                      onChange={(value: any) => {
                        fileContext.setStaffIds(value.map((v: IUser) => v.userId));
                        props.setIsDirty(true);
                      }}
                      value={selectedStaff}
                      getOptionValue={(option: IUser) => option.userId}
                      getOptionLabel={(option: IUser) =>
                        option.lastName + ', ' + option.firstName + (option.active ? '' : ' (Inactive)')
                      }
                      isDisabled={!props.isEditingAllowed || (fileContext.isConfidential && !myUser.isSuperAdmin)}
                      isClearable={true}
                      isMulti={true}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormGroup className={'w-100 mb-2'}>
                    <Label>File Details/Background</Label>
                    <Input
                      type='textarea'
                      placeholder=''
                      style={{ minHeight: '226px' }}
                      name='name'
                      value={fileContext.details}
                      disabled={!props.isEditingAllowed}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        fileContext.setDetails(e.target.value);
                        props.setIsDirty(true);
                      }}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col sm={12} md={12}>
          <Card className={'mb-4'}>
            <CardHeader>Required Documents</CardHeader>
            <CardBody>
              <Row className='square border-bottom mb-2'>
                <Col md={6}>
                  <Row>
                    <Col md={4}>
                      <FormGroup className={'w-100 mb-2 '}>
                        <Label for={'completed-date'}>Needs Assessment</Label>
                        <Select
                          styles={disabledSelectStylesWithMenu}
                          name={'needsAssessmentDocumentStatus'}
                          placeholder={<span className='placeholder'>Select...</span>}
                          options={postMajorityDocumentStatuses}
                          value={
                            postMajorityDocumentStatuses.find(
                              (p: IPostMajorityDocumentStatus) =>
                                p.postMajorityDocumentStatusId ===
                                fileContext.postMajoritySupportServicesFields?.needsAssessmentDocumentStatus
                            ) || null
                          }
                          onChange={(value: any) => {
                            fileContext.setPostMajoritySupportServicesFields(
                              (s) =>
                                ({
                                  ...s,
                                  needsAssessmentDocumentStatus: value?.postMajorityDocumentStatusId,
                                } as IPostMajoritySupportServicesFields)
                            );
                            props.setIsDirty(true);
                          }}
                          getOptionValue={(option: IPostMajorityDocumentStatus) => option.postMajorityDocumentStatusId}
                          getOptionLabel={(option: IPostMajorityDocumentStatus) => option.name}
                          isDisabled={!props.isEditingAllowed}
                          isClearable={false}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className={'w-100'}>
                        {props.isEditingAllowed && (
                          <FileUpload
                            isUploading={false}
                            onDrop={handleFileDrop(NEEDS_ASSESSMENT_DOCUMENT_TYPE)}
                            uploadSuccessful={false}
                          />
                        )}
                        {props.postMajorityDocumentsToUpload.filter(
                          (d) => d.documentTypeId === NEEDS_ASSESSMENT_DOCUMENT_TYPE
                        ).length > 0 && (
                          <FormGroup className={'w-100'}>
                            <ListGroup className={'my-3'}>
                              {props.postMajorityDocumentsToUpload
                                ?.filter((doc) => doc.documentTypeId === NEEDS_ASSESSMENT_DOCUMENT_TYPE)
                                .map((d, i) => (
                                  <DocumentListItem
                                    key={i}
                                    name={d.file.name}
                                    onDelete={() => removeDocumentToUpload(d.file)}
                                    showDelete={true}
                                  />
                                ))}
                            </ListGroup>
                            <FormText color='muted' className={'my-3'}>
                              New documents will be uploaded when this file is saved.
                            </FormText>
                          </FormGroup>
                        )}
                        {props.postMajorityDocuments &&
                          props.postMajorityDocuments.filter((d) => d.documentTypeId === NEEDS_ASSESSMENT_DOCUMENT_TYPE)
                            .length > 0 && (
                            <ListGroup className={'my-3'}>
                              {props.postMajorityDocuments
                                ?.filter((doc) => doc.documentTypeId === NEEDS_ASSESSMENT_DOCUMENT_TYPE)
                                .map((d, i) => (
                                  <li key={d.documentId}>
                                    <Button
                                      color={'link'}
                                      className={'p-0'}
                                      onClick={() => downloadDocument(d.uploadId)}
                                      disabled={!d.uploadId}
                                      style={{ wordBreak: 'break-all', textAlign: 'left' }}
                                      title={'Download "' + d.fileName + '"'}
                                    >
                                      {d.fileName}
                                    </Button>
                                  </li>
                                ))}
                            </ListGroup>
                          )}
                      </FormGroup>
                    </Col>
                  </Row>
                </Col>
                <Col md={6}>
                  <Row>
                    <Col md={4}>
                      <FormGroup className={'w-100 mb-2 '}>
                        <Label for={'completed-date'}>Case Plan</Label>
                        <Select
                          styles={disabledSelectStylesWithMenu}
                          name={'casePlanDocumentStatus'}
                          placeholder={<span className='placeholder'>Select...</span>}
                          options={postMajorityDocumentStatuses}
                          value={
                            postMajorityDocumentStatuses.find(
                              (p: IPostMajorityDocumentStatus) =>
                                p.postMajorityDocumentStatusId ===
                                fileContext.postMajoritySupportServicesFields?.casePlanDocumentStatus
                            ) || null
                          }
                          onChange={(value: any) => {
                            fileContext.setPostMajoritySupportServicesFields(
                              (s) =>
                                ({
                                  ...s,
                                  casePlanDocumentStatus: value?.postMajorityDocumentStatusId,
                                } as IPostMajoritySupportServicesFields)
                            );
                            props.setIsDirty(true);
                          }}
                          getOptionValue={(option: IPostMajorityDocumentStatus) => option.postMajorityDocumentStatusId}
                          getOptionLabel={(option: IPostMajorityDocumentStatus) => option.name}
                          isDisabled={!props.isEditingAllowed}
                          isClearable={false}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className={'w-100'}>
                        {props.isEditingAllowed && (
                          <FileUpload
                            isUploading={false}
                            onDrop={handleFileDrop(CASE_PLAN_DOCUMENT_TYPE)}
                            uploadSuccessful={false}
                          />
                        )}
                        {props.postMajorityDocumentsToUpload.filter((d) => d.documentTypeId === CASE_PLAN_DOCUMENT_TYPE)
                          .length > 0 && (
                          <FormGroup className={'w-100'}>
                            <ListGroup className={'my-3'}>
                              {props.postMajorityDocumentsToUpload
                                ?.filter((doc) => doc.documentTypeId === CASE_PLAN_DOCUMENT_TYPE)
                                .map((d, i) => (
                                  <DocumentListItem
                                    key={i}
                                    name={d.file.name}
                                    onDelete={() => removeDocumentToUpload(d.file)}
                                    showDelete={true}
                                  />
                                ))}
                            </ListGroup>
                            <FormText color='muted' className={'my-3'}>
                              New documents will be uploaded when this file is saved.
                            </FormText>
                          </FormGroup>
                        )}
                        {props.postMajorityDocuments &&
                          props.postMajorityDocuments.filter((d) => d.documentTypeId === CASE_PLAN_DOCUMENT_TYPE)
                            .length > 0 && (
                            <ListGroup className={'my-3'}>
                              {props.postMajorityDocuments
                                ?.filter((doc) => doc.documentTypeId === CASE_PLAN_DOCUMENT_TYPE)
                                .map((d, i) => (
                                  <li key={d.documentId}>
                                    <Button
                                      color={'link'}
                                      className={'p-0'}
                                      onClick={() => downloadDocument(d.uploadId)}
                                      disabled={!d.uploadId}
                                      style={{ wordBreak: 'break-all', textAlign: 'left' }}
                                      title={'Download "' + d.fileName + '"'}
                                    >
                                      {d.fileName}
                                    </Button>
                                  </li>
                                ))}
                            </ListGroup>
                          )}
                      </FormGroup>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className='square border-bottom mb-2'>
                <Col md={6}>
                  <Row>
                    <Col md={4}>
                      <FormGroup className={'w-100'}>
                        <Label for={'completed-date'}>Program Agreement</Label>
                        <Select
                          styles={disabledSelectStylesWithMenu}
                          name={'programAgreementDocumentStatus'}
                          placeholder={<span className='placeholder'>Select...</span>}
                          options={postMajorityDocumentStatuses}
                          value={
                            postMajorityDocumentStatuses.find(
                              (p: IPostMajorityDocumentStatus) =>
                                p.postMajorityDocumentStatusId ===
                                fileContext.postMajoritySupportServicesFields?.programAgreementDocumentStatus
                            ) || null
                          }
                          onChange={(value: any) => {
                            fileContext.setPostMajoritySupportServicesFields(
                              (s) =>
                                ({
                                  ...s,
                                  programAgreementDocumentStatus: value?.postMajorityDocumentStatusId,
                                } as IPostMajoritySupportServicesFields)
                            );
                            props.setIsDirty(true);
                          }}
                          getOptionValue={(option: IPostMajorityDocumentStatus) => option.postMajorityDocumentStatusId}
                          getOptionLabel={(option: IPostMajorityDocumentStatus) => option.name}
                          isDisabled={!props.isEditingAllowed}
                          isClearable={false}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className={'w-100'}>
                        {props.isEditingAllowed && (
                          <FileUpload
                            isUploading={false}
                            onDrop={handleFileDrop(PROGRAM_AGREEMENT_DOCUMENT_TYPE)}
                            uploadSuccessful={false}
                          />
                        )}
                        {props.postMajorityDocumentsToUpload.filter(
                          (d) => d.documentTypeId === PROGRAM_AGREEMENT_DOCUMENT_TYPE
                        ).length > 0 && (
                          <FormGroup className={'w-100'}>
                            <ListGroup className={'my-3'}>
                              {props.postMajorityDocumentsToUpload
                                ?.filter((doc) => doc.documentTypeId === PROGRAM_AGREEMENT_DOCUMENT_TYPE)
                                .map((d, i) => (
                                  <DocumentListItem
                                    key={i}
                                    name={d.file.name}
                                    onDelete={() => removeDocumentToUpload(d.file)}
                                    showDelete={true}
                                  />
                                ))}
                            </ListGroup>
                            <FormText color='muted' className={'my-3'}>
                              New documents will be uploaded when this file is saved.
                            </FormText>
                          </FormGroup>
                        )}
                        {props.postMajorityDocuments &&
                          props.postMajorityDocuments.filter(
                            (d) => d.documentTypeId === PROGRAM_AGREEMENT_DOCUMENT_TYPE
                          ).length > 0 && (
                            <ListGroup className={'my-3'}>
                              {props.postMajorityDocuments
                                ?.filter((doc) => doc.documentTypeId === PROGRAM_AGREEMENT_DOCUMENT_TYPE)
                                .map((d, i) => (
                                  <li key={d.documentId}>
                                    <Button
                                      color={'link'}
                                      className={'p-0'}
                                      onClick={() => downloadDocument(d.uploadId)}
                                      disabled={!d.uploadId}
                                      style={{ wordBreak: 'break-all', textAlign: 'left' }}
                                      title={'Download "' + d.fileName + '"'}
                                    >
                                      {d.fileName}
                                    </Button>
                                  </li>
                                ))}
                            </ListGroup>
                          )}
                      </FormGroup>
                    </Col>
                  </Row>
                </Col>
                <Col md={6}>
                  <Row>
                    <Col md={4}>
                      <FormGroup className={'w-100'}>
                        <Label for={'completed-date'}>EFT</Label>
                        <Select
                          styles={disabledSelectStylesWithMenu}
                          name={'eftDocumentStatus'}
                          placeholder={<span className='placeholder'>Select...</span>}
                          options={postMajorityDocumentStatuses}
                          value={
                            postMajorityDocumentStatuses.find(
                              (p: IPostMajorityDocumentStatus) =>
                                p.postMajorityDocumentStatusId ===
                                fileContext.postMajoritySupportServicesFields?.eftDocumentStatus
                            ) || null
                          }
                          onChange={(value: any) => {
                            fileContext.setPostMajoritySupportServicesFields(
                              (s) =>
                                ({
                                  ...s,
                                  eftDocumentStatus: value?.postMajorityDocumentStatusId,
                                } as IPostMajoritySupportServicesFields)
                            );
                            props.setIsDirty(true);
                          }}
                          getOptionValue={(option: IPostMajorityDocumentStatus) => option.postMajorityDocumentStatusId}
                          getOptionLabel={(option: IPostMajorityDocumentStatus) => option.name}
                          isDisabled={!props.isEditingAllowed}
                          isClearable={false}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className={'w-100'}>
                        {props.isEditingAllowed && (
                          <FileUpload
                            isUploading={false}
                            onDrop={handleFileDrop(EFT_DOCUMENT_TYPE)}
                            uploadSuccessful={false}
                          />
                        )}
                        {props.postMajorityDocumentsToUpload.filter((d) => d.documentTypeId === EFT_DOCUMENT_TYPE)
                          .length > 0 && (
                          <FormGroup className={'w-100'}>
                            <ListGroup className={'my-3'}>
                              {props.postMajorityDocumentsToUpload
                                ?.filter((doc) => doc.documentTypeId === EFT_DOCUMENT_TYPE)
                                .map((d, i) => (
                                  <DocumentListItem
                                    key={i}
                                    name={d.file.name}
                                    onDelete={() => removeDocumentToUpload(d.file)}
                                    showDelete={true}
                                  />
                                ))}
                            </ListGroup>
                            <FormText color='muted' className={'my-3'}>
                              New documents will be uploaded when this file is saved.
                            </FormText>
                          </FormGroup>
                        )}
                        {props.postMajorityDocuments &&
                          props.postMajorityDocuments.filter((d) => d.documentTypeId === EFT_DOCUMENT_TYPE).length >
                            0 && (
                            <ListGroup className={'my-3'}>
                              {props.postMajorityDocuments
                                ?.filter((doc) => doc.documentTypeId === EFT_DOCUMENT_TYPE)
                                .map((d, i) => (
                                  <li key={d.documentId}>
                                    <Button
                                      color={'link'}
                                      className={'p-0'}
                                      onClick={() => downloadDocument(d.uploadId)}
                                      disabled={!d.uploadId}
                                      style={{ wordBreak: 'break-all', textAlign: 'left' }}
                                      title={'Download "' + d.fileName + '"'}
                                    >
                                      {d.fileName}
                                    </Button>
                                  </li>
                                ))}
                            </ListGroup>
                          )}
                      </FormGroup>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Row>
                    <Col md={4}>
                      <FormGroup className={'w-100 mb-2 '}>
                        <Label for={'completed-date'}>TSS Application/Consent</Label>
                        <Select
                          styles={disabledSelectStylesWithMenu}
                          name={'tssApplicationConsentDocumentStatus'}
                          placeholder={<span className='placeholder'>Select...</span>}
                          options={postMajorityDocumentStatuses}
                          value={
                            postMajorityDocumentStatuses.find(
                              (p: IPostMajorityDocumentStatus) =>
                                p.postMajorityDocumentStatusId ===
                                fileContext.postMajoritySupportServicesFields?.tssApplicationConsentDocumentStatus
                            ) || null
                          }
                          onChange={(value: any) => {
                            fileContext.setPostMajoritySupportServicesFields(
                              (s) =>
                                ({
                                  ...s,
                                  tssApplicationConsentDocumentStatus: value?.postMajorityDocumentStatusId,
                                } as IPostMajoritySupportServicesFields)
                            );
                            props.setIsDirty(true);
                          }}
                          getOptionValue={(option: IPostMajorityDocumentStatus) => option.postMajorityDocumentStatusId}
                          getOptionLabel={(option: IPostMajorityDocumentStatus) => option.name}
                          isDisabled={!props.isEditingAllowed}
                          isClearable={false}
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className={'w-100'}>
                        {props.isEditingAllowed && (
                          <FileUpload
                            isUploading={false}
                            onDrop={handleFileDrop(TSS_APPLICATION_CONSENT_DOCUMENT_TYPE)}
                            uploadSuccessful={false}
                          />
                        )}
                        {props.postMajorityDocumentsToUpload.filter(
                          (d) => d.documentTypeId === TSS_APPLICATION_CONSENT_DOCUMENT_TYPE
                        ).length > 0 && (
                          <FormGroup className={'w-100'}>
                            <ListGroup className={'my-3'}>
                              {props.postMajorityDocumentsToUpload
                                ?.filter((doc) => doc.documentTypeId === TSS_APPLICATION_CONSENT_DOCUMENT_TYPE)
                                .map((d, i) => (
                                  <DocumentListItem
                                    key={i}
                                    name={d.file.name}
                                    onDelete={() => removeDocumentToUpload(d.file)}
                                    showDelete={true}
                                  />
                                ))}
                            </ListGroup>
                            <FormText color='muted' className={'my-3'}>
                              New documents will be uploaded when this file is saved.
                            </FormText>
                          </FormGroup>
                        )}
                        {props.postMajorityDocuments &&
                          props.postMajorityDocuments.filter(
                            (d) => d.documentTypeId === TSS_APPLICATION_CONSENT_DOCUMENT_TYPE
                          ).length > 0 && (
                            <ListGroup className={'my-3'}>
                              {props.postMajorityDocuments
                                ?.filter((doc) => doc.documentTypeId === TSS_APPLICATION_CONSENT_DOCUMENT_TYPE)
                                .map((d, i) => (
                                  <li key={d.documentId}>
                                    <Button
                                      color={'link'}
                                      className={'p-0'}
                                      onClick={() => downloadDocument(d.uploadId)}
                                      disabled={!d.uploadId}
                                      style={{ wordBreak: 'break-all', textAlign: 'left' }}
                                      title={'Download "' + d.fileName + '"'}
                                    >
                                      {d.fileName}
                                    </Button>
                                  </li>
                                ))}
                            </ListGroup>
                          )}
                      </FormGroup>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};
